import { Injectable } from '@angular/core';
import { LeafletShape } from '@records/records-search/esri-map/esri-map.model';
import { IgnoreHistoryService } from '@records/records-search/shared/ignore-history.service';
import { DataObject } from '@shared/models/data-object.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import safeJsonStringify from 'safe-json-stringify';
import { FlatSearchResults } from '../../records-search/shared/search-results.model';
import {
  DEFAULT_ENTITY,
  DEFAULT_MAP_STATE,
  DEFAULT_QUERY_STATE,
  ENTITY,
  MAP_STATE,
  MAX_LOCAL_STORAGE_SIZE_PER_PAGE,
  QUERY_STATE,
} from './search-state.constants';
import { loadEntityFromSession, loadMapStateFromSession, loadQueryStateFromSession } from './search-state.helpers';
import { KpisResult, DEFAULT_KPIS_RESULT } from '@shared/components/kpi-summary/kpi-summary-columns.helper';
@Injectable({ providedIn: 'root' })
export class RecordsSearchStateService {
  private querySrc = new BehaviorSubject<any>({});
  public query = this.querySrc.asObservable();

  private mapStateSrc = new BehaviorSubject<any>({});
  public map = this.mapStateSrc.asObservable();

  private _restartMap = new Subject<boolean>();
  restartMap = this._restartMap.asObservable();

  private searchSrc = new BehaviorSubject<boolean>(false);
  // we only send ticks on truthy beats
  fetchSearch$ = this.searchSrc.asObservable().pipe(filter(Boolean));

  private resultsSrc = new BehaviorSubject<FlatSearchResults>({ results: [], totalCount: 0 });
  public results = this.resultsSrc.asObservable();

  private kpisSrc = new BehaviorSubject<KpisResult>(DEFAULT_KPIS_RESULT);
  public kpis = this.kpisSrc.asObservable();

  private _clearSignal = new Subject<void>();
  clearRequest$ = this._clearSignal.asObservable();

  private templateSubject = new BehaviorSubject<boolean>(false);
  public fetchTemplate$ = this.templateSubject.asObservable().pipe(filter(Boolean));

  private userColumnsSrc = new BehaviorSubject<any>([]);
  public userColumns = this.userColumnsSrc.asObservable();

  private _subset;

  private _forceUpdateSignal = new Subject<DataObject>();
  public forceUpdateSignal = this._forceUpdateSignal.asObservable();
  public applyPropertyTypeFilter = false;

  private static readonly DEFAULT_VIEW = 'both';
  private static readonly ENTITY_VIEW_KEY = 'miq_entity_view_preference';
  constructor(private ignoreHistoryService: IgnoreHistoryService) {
    const entity = loadEntityFromSession();
    this.querySrc.next(loadQueryStateFromSession(entity));
    this.mapStateSrc.next(loadMapStateFromSession(entity));
  }

  /**
   * Gets the global view preference that applies to all entities
   * @returns The global view option or 'both' as default
   */
  private getEntityViewPrefs(): Record<string, string> {
    return JSON.parse(localStorage.getItem(RecordsSearchStateService.ENTITY_VIEW_KEY) || '{}');
  }

  public getEntityViewPreference(entity: string): string {
    const prefs = this.getEntityViewPrefs();
    return prefs[entity] || RecordsSearchStateService.DEFAULT_VIEW;
  }

  public setEntityViewPreference(entity: string, viewOption: string): void {
    const prefs = this.getEntityViewPrefs();
    prefs[entity] = viewOption;
    localStorage.setItem(RecordsSearchStateService.ENTITY_VIEW_KEY, JSON.stringify(prefs));
  }

  public setUserColumns(columns) {
    this.userColumnsSrc.next(columns);
  }

  public get queryState() {
    return this.querySrc.getValue();
  }

  public loadQueryStateByEntity(entity) {
    return this.queryState[entity] || { ...DEFAULT_QUERY_STATE, entity };
  }

  public get activeQueryState() {
    return this.queryState[this.subset];
  }

  public get resultsState() {
    return this.resultsSrc.getValue();
  }

  get subset() {
    return this._subset || localStorage.getItem(ENTITY) || DEFAULT_ENTITY;
  }

  set subset(value) {
    this._subset = value;
    localStorage.setItem(ENTITY, value);
  }

  sendClearSignal() {
    this._clearSignal.next();
  }

  public setMapState(mapState) {
    const { entity } = this.activeQueryState;
    const newMapState = { ...this.mapState, [entity]: mapState };
    const serializableState = Object.keys(newMapState)
      .map(key => ({ key, value: preventCirular(newMapState[key]) }))
      .reduce((a, b) => ({ ...a, [b.key]: { ...b.value } }), {});
    const newSerializedMapState = safeJsonStringify(serializableState);

    if (newSerializedMapState.length < MAX_LOCAL_STORAGE_SIZE_PER_PAGE) {
      localStorage.setItem(MAP_STATE, newSerializedMapState);
    }
    this.mapStateSrc.next(newMapState);
  }

  public loadMapStateByEntity(entity) {
    return this.mapState[entity] || { ...DEFAULT_MAP_STATE };
  }

  get activeMapState() {
    return this.mapState[this.subset];
  }

  public get mapState() {
    return this.mapStateSrc.getValue();
  }

  /**
   * Saves the search state in local storage
   * @param state {DataObject}
   */
  public setQuery(state: DataObject) {
    let { filters } = state;
    const { ...nextState } = state;
    this.subset = state.entity;
    const currentSubsetState = this.activeQueryState || DEFAULT_QUERY_STATE;
    filters = filters ?? currentSubsetState.filters;

    this.applyPropertyTypeFilter = true;

    const newState = {
      ...this.queryState,
      [state.entity]: this._fixQueryStateAndSorting({
        ...currentSubsetState,
        ...nextState,
        filters,
      }),
    };

    localStorage.setItem(QUERY_STATE, safeJsonStringify(newState));
    this.querySrc.next(newState);
  }

  /**
   * Cleans up some attributes of given state
   *
   * @param options
   * @returns DataObject
   */
  private _fixQueryStateAndSorting(searchState: DataObject): DataObject {
    const { sort, multiSort, ...nextState } = searchState;
    return { ...nextState, ...this._buildMultiSort({ sort, multiSort }) };
  }

  /**
   * Ensures sort (single and deprecated attribute) gets transformed to multiSort AND ensures presence/absence of multiSort as required
   * if null or empty it gets removed otherwise it's added
   *
   * @param state
   * @returns DataObject & { multiSort: DataObject[]}
   */
  private _buildMultiSort(state: DataObject): { multiSort: DataObject[] } {
    const { sort, multiSort } = state;
    let nextMultiSort = multiSort;
    if (sort != null) {
      nextMultiSort = multiSort == null ? [sort] : [...multiSort, sort];
    }
    return nextMultiSort != null && nextMultiSort?.length > 0 ? { multiSort: nextMultiSort } : null;
  }

  /**
   * Turns on/off ignoreHistory flag for search calls, updates the query state and emit a
   * beat to perform a search
   * @param state {DataObject}
   * @param saveToHistorySearch {boolean}
   */
  public setQueryAndSearch(state: DataObject, saveToHistorySearch = false, isGroupedView = false) {
    if (saveToHistorySearch) {
      this.ignoreHistoryService.off();
    } else {
      this.ignoreHistoryService.on();
    }
    this.setQuery({ ...state, after: isGroupedView ? state.after : undefined });
    this.searchSrc.next(true);
  }

  public reloadUserCatalogTemplate(): void {
    this.templateSubject.next(true);
  }

  public setMapStateAndRefresh(state) {
    this.setMapState(state);
    this._restartMap.next(true);
  }

  public setResults(r: FlatSearchResults) {
    this.resultsSrc.next(r);
  }

  public setKPIs(r: KpisResult) {
    this.kpisSrc.next(r);
  }

  public sendForceUpdateSignal(customEvt?: DataObject) {
    this._forceUpdateSignal.next(customEvt);
  }

  /**
   * Refresh the search with the current criteria
   */
  public refresh() {
    this.searchSrc.next(true);
  }
}

/**
 * To remove editing - _poly CIRCULAR dependency
 * @param { shape, layer }
 */
const removeShapeCircular = ({ shape, layer }) => {
  if (shape === LeafletShape.Polygon) {
    const { options, _bounds, _latlngs } = layer;
    layer = { options, _bounds, _latlngs };
  } else {
    const { options, _latlng, _mRadius } = layer;
    layer = { options, _latlng, _mRadius };
  }
  return { shape, layer };
};

/**
 * If shape present then invoke removeShapeCircular function
 */
const preventCirular = state => {
  const { shape } = state;
  if (shape) {
    return { ...state, shape: removeShapeCircular(shape) };
  }
  return state;
};
